import { useQuery } from '@tanstack/react-query';
import { Activity, Building2, Menu, Search, ShieldCheck } from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import toast from 'react-hot-toast';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { logout as apiLogout } from '@/api/auth';
import {
  getOrganizations,
  organizationsQueryKey,
  type Organization,
} from '@/api/organizations';
import HealthGuardNavIndicator from '@/components/common/HealthGuardNavIndicator';
import { IconButton, Sheet } from '@/components/ui';
import { cx, focusRingClass, interactiveClass } from '@/components/ui/shared';
import { labRoutesEnabled } from '@/config/features';
import {
  canSearchConsoleDevices,
  getConsoleLandingPath,
  getConsolePageTitle,
  getVisibleConsoleSections,
  type ConsoleRouteContext,
} from '@/navigation/consoleNavigation';
import { consoleRoutePatterns } from '@/navigation/consoleRoutePaths';
import {
  useActingOrgId,
  useActingOrgName,
  useActualOrgId,
  useAuthStore,
  useIsImpersonating,
} from '@/stores/authStore';
import ConsoleAccountMenu from './ConsoleAccountMenu';
import ConsoleNavigationPanel from './ConsoleNavigationPanel';
import ConsoleNeedsYouIndicator from './ConsoleNeedsYouIndicator';
import ConsoleScopeSwitcher from './ConsoleScopeSwitcher';
import ConsoleTaskSearch from './ConsoleTaskSearch';
import { useConsoleScopeTransition } from './useConsoleScopeTransition';

interface ConsoleShellProps {
  children?: ReactNode;
}

const isTextEntryTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'SELECT' ||
    target.tagName === 'TEXTAREA'
  );
};

export default function ConsoleShell({ children }: ConsoleShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isOwner = useAuthStore((state) => state.isOwner);
  const actualOrgId = useActualOrgId();
  const actingOrgId = useActingOrgId();
  const actingOrgName = useActingOrgName();
  const isImpersonating = useIsImpersonating();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const {
    enterOrganization,
    isSwitchingScope,
    returnToPlatform,
  } = useConsoleScopeTransition();

  const isOrganizationScope = !isOwner || isImpersonating;
  const routeContext = useMemo<ConsoleRouteContext>(
    () => ({
      userRole: user?.role ?? null,
      isOwner,
      isOrganizationScope,
      enableLabRoutes: labRoutesEnabled,
    }),
    [isOrganizationScope, isOwner, user?.role],
  );
  const sections = useMemo(
    () => getVisibleConsoleSections(routeContext),
    [routeContext],
  );
  const homePath = getConsoleLandingPath(routeContext);
  const canSearchDevices = canSearchConsoleDevices(routeContext);

  const organizationsQuery = useQuery<Organization[], Error>({
    queryKey: organizationsQueryKey,
    queryFn: getOrganizations,
    enabled: isOwner && scopeOpen,
  });

  useEffect(() => {
    setNavigationOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const titleOrganizationName =
    isOwner && !isImpersonating ? 'Platform' : (actingOrgName ?? user?.organization_name ?? null);
  useEffect(() => {
    document.title = getConsolePageTitle(location.pathname, titleOrganizationName);
  }, [location.pathname, titleOrganizationName]);

  useEffect(() => {
    const openTaskSearch = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTextEntryTarget(event.target)) return;
      event.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener('keydown', openTaskSearch);
    return () => window.removeEventListener('keydown', openTaskSearch);
  }, []);

  if (!user) {
    return <Navigate to={consoleRoutePatterns.login} replace />;
  }

  const organizationName =
    actingOrgName ??
    user.organization_name ??
    (actingOrgId ? `Organization ${actingOrgId}` : null);
  const activeScopeLabel =
    isOwner && !isImpersonating
      ? 'Platform'
      : organizationName ?? 'Organization unavailable';
  const activeScopeMode =
    isOwner && !isImpersonating ? 'Platform scope' : 'Organization scope';
  const ownerPath = consoleRoutePatterns.ownerBase;
  const isOwnerPage =
    location.pathname === ownerPath || location.pathname.startsWith(`${ownerPath}/`);

  const enterOrganizationFromSheet = async (organization: Organization) => {
    await enterOrganization(organization);
    setScopeOpen(false);
  };

  const returnToPlatformFromSheet = async () => {
    await returnToPlatform();
    setScopeOpen(false);
  };

  const handleLogout = async () => {
    try {
      await apiLogout();
      navigate(consoleRoutePatterns.login);
      toast.success('Signed out');
    } catch {
      toast.error('Sign out could not be confirmed. Try again.');
    }
  };


  const scopeChip = (
    <div
      data-console-scope={isOrganizationScope ? 'organization' : 'platform'}
      className={cx(
        'flex min-h-control-comfortable min-w-0 items-center gap-2 rounded-control border px-3',
        isOrganizationScope
          ? 'border-border-subtle bg-surface text-ink'
          : 'border-status-blocked-border bg-status-blocked-surface text-status-blocked',
      )}
    >
      {isOrganizationScope ? (
        <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
      ) : (
        <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      <span className="min-w-0 leading-tight">
        <span className="sr-only text-meta text-ink-muted sm:not-sr-only sm:block">
          {activeScopeMode}
        </span>
        <span className="block max-w-24 truncate text-control font-semibold sm:max-w-40">
          {activeScopeLabel}
        </span>
      </span>
      {isOwner ? (
        <button
          type="button"
          onClick={() => setScopeOpen(true)}
          className={cx(
            focusRingClass,
            'ml-1 min-h-9 shrink-0 rounded-control px-2 text-meta font-semibold underline-offset-2 hover:underline',
          )}
        >
          Change scope
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="min-h-screen bg-canvas text-ink" data-console-shell="unified">
      <a
        href="#console-content"
        className={cx(
          focusRingClass,
          'fixed left-3 top-3 z-[70] -translate-y-24 rounded-control bg-inverse-surface px-4 py-3 text-control font-semibold text-on-inverse focus:translate-y-0',
        )}
      >
        Skip to console content
      </a>

      <header className="sticky top-0 z-30 border-b border-border-subtle bg-canvas/95 backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-content items-center gap-3 px-page-inline-compact sm:px-page-inline-sm lg:gap-6 lg:px-page-inline-lg">
          <div className="lg:hidden">
            <IconButton
              label="Open console navigation"
              onClick={() => setNavigationOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </IconButton>
          </div>

          <Link
            to={homePath}
            aria-label="SoundOps home"
            className={cx(
              focusRingClass,
              'flex shrink-0 items-center gap-2 rounded-control px-1 py-1 text-ink-strong',
            )}
          >
            <Activity className="h-5 w-5 text-accent" strokeWidth={2.4} aria-hidden="true" />
            <span className="hidden text-subsection-title font-extrabold tracking-tight sm:block">
              SoundOps
            </span>
          </Link>

          <div data-console-navigation="desktop" className="hidden min-w-0 flex-1 lg:block">
            <ConsoleNavigationPanel
              context={routeContext}
              currentPath={location.pathname}
              sections={sections}
              variant="bar"
            />
          </div>

          <div className="min-w-0 flex-1 lg:hidden" />

          {isOrganizationScope ? (
            <>
              <HealthGuardNavIndicator />
              <ConsoleNeedsYouIndicator />
            </>
          ) : null}

          <button
            type="button"
            aria-label="Search"
            onClick={() => setSearchOpen(true)}
            className={cx(
              focusRingClass,
              interactiveClass,
              'flex min-h-control-comfortable shrink-0 items-center gap-2 rounded-control border border-border-subtle bg-surface px-3 text-control font-semibold text-ink-muted hover:border-border-strong hover:text-ink',
            )}
            aria-keyshortcuts="/"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            <span className="hidden xl:inline">Search</span>
            <kbd className="so-data-text hidden rounded border border-border-subtle px-1.5 text-meta md:inline">
              /
            </kbd>
          </button>

          {scopeChip}

          <ConsoleAccountMenu
            activeScopeLabel={activeScopeLabel}
            onLogout={handleLogout}
            user={user}
          />
        </div>
      </header>

      <main id="console-content" className="min-w-0" tabIndex={-1}>
        <div
          className={cx(
            isOwnerPage &&
              'mx-auto max-w-content px-page-inline-compact py-8 sm:px-page-inline-sm lg:px-page-inline-lg',
          )}
        >
          {children ?? <Outlet />}
        </div>
      </main>

      <Sheet
        isOpen={navigationOpen}
        onClose={() => setNavigationOpen(false)}
        title="Console tasks"
        description={`${activeScopeMode}: ${activeScopeLabel}`}
        side="left"
      >
        <ConsoleNavigationPanel
          context={routeContext}
          currentPath={location.pathname}
          sections={sections}
          onNavigate={() => setNavigationOpen(false)}
          variant="list"
        />
      </Sheet>

      <Sheet
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        title={canSearchDevices ? 'Find a device or page' : 'Find a page'}
        description={
          canSearchDevices
            ? 'Jump to a device or to any page you can open.'
            : 'Jump to any page you can open.'
        }
        initialFocusRef={searchInputRef}
      >
        <ConsoleTaskSearch
          ref={searchInputRef}
          context={routeContext}
          onNavigate={() => setSearchOpen(false)}
        />
      </Sheet>

      {isOwner ? (
        <Sheet
          isOpen={scopeOpen}
          onClose={() => setScopeOpen(false)}
          title="Change console scope"
          description="Choose platform-wide work or deliberately enter one organization."
        >
          <ConsoleScopeSwitcher
            activeOrganizationId={actingOrgId ?? actualOrgId}
            activeOrganizationName={organizationName}
            error={organizationsQuery.error ?? null}
            isImpersonating={isImpersonating}
            isLoading={organizationsQuery.isLoading}
            isSwitching={isSwitchingScope}
            organizations={organizationsQuery.data ?? []}
            onEnterOrganization={enterOrganizationFromSheet}
            onRetry={() => void organizationsQuery.refetch()}
            onReturnToPlatform={returnToPlatformFromSheet}
          />
        </Sheet>
      ) : null}
    </div>
  );
}
